/**
 * A personal API key's derived status (TO-516). Never persisted: computed from
 * the revocation stamp/reason, the expiry and the idle anchor at read time.
 */
export enum ApiKeyStatus {
	ACTIVE = 'ACTIVE',
	REVOKED = 'REVOKED',
	EXPIRED = 'EXPIRED',
	IDLE = 'IDLE',
}

/**
 * Why a key was revoked (TO-516). Persisted as `varchar(32)` in
 * `users_api_keys.revoked_reason` — never a Postgres ENUM. `USER_REVOKED` is the
 * owner's explicit revoke; `EXPIRED` / `IDLE` are stamped by the daily sweep.
 */
export enum ApiKeyRevokedReason {
	USER_REVOKED = 'USER_REVOKED',
	EXPIRED = 'EXPIRED',
	IDLE = 'IDLE',
}

/** A personal API key as stored (TO-516). The plaintext is never part of it. */
export interface IApiKey {
	apiKeyId: string
	tenantId: string
	userId: string
	name: string
	/** Displayable `tdy_<stage>_` + the first 4 secret characters. Not secret. */
	keyPrefix: string
	/** SHA-256 hex of the plaintext — the only stored secret, never returned. */
	keyHash: string
	lastUsedAt: Date | null
	expiresAt: Date
	revokedAt: Date | null
	revokedReason: ApiKeyRevokedReason | null
	createdAt: Date
	updatedAt: Date
	deletedAt: Date | null
}

/** The public shape of a key (`GET /users/me/api-keys`). No hash, no plaintext. */
export interface ApiKeyDto {
	apiKeyId: string
	name: string
	keyPrefix: string
	status: ApiKeyStatus
	createdAt: string
	expiresAt: string
	lastUsedAt: string | null
	revokedAt: string | null
	revokedReason: ApiKeyRevokedReason | null
}

/** The create response — the ONLY shape that ever carries the plaintext. */
export interface CreatedApiKeyDto extends ApiKeyDto {
	plaintextKey: string
}
